import type { Draft } from './types';

/**
 * La bozza in composizione, salvata fuori dalla memoria di React.
 *
 * Serve perché ogni modo in cui questa schermata può finire male costa lo
 * stesso prezzo: quello che l'operatore aveva scritto. Una sessione scaduta,
 * un ricaricamento per sbloccare qualcosa, un tasto indietro, un browser che
 * si chiude — e chi stava scrivendo alle tre di notte ricomincia da capo,
 * proprio nel momento in cui ricominciare da capo costa di più.
 *
 * `sessionStorage` e non `localStorage`: sopravvive al ricaricamento, che è il
 * caso che ci interessa, e muore con la scheda. Su un computer del COC usato
 * da più persone una bozza che resta lì per giorni è un problema diverso, e
 * non è quello che stiamo risolvendo.
 *
 * La chiave porta il codice del Comune: se un domani un operatore agisse per
 * due enti, le bozze non si mescolerebbero.
 */

const PREFIX = 'am-console:bozza:';

export interface StoredDraft {
  draft: Draft;
  preset: string;
  savedAt: string;
  /**
   * La chiave di questo TENTATIVO di pubblicazione.
   *
   * Vive con la bozza, e non nello stato di React, per l'unico caso in cui
   * serve davvero: la risposta di `publish` si perde (rete che cade, tab
   * ricaricata, telefono che si spegne) e chi ha appena scritto «acqua non
   * potabile» ripreme. Con la chiave in memoria, quel secondo tentativo è una
   * pubblicazione NUOVA e manda una seconda push; con la chiave qui, il server
   * riconosce il tentativo e restituisce l'avviso che ha già creato.
   *
   * Si azzera insieme alla bozza (`clearDraft`), cioè quando la pubblicazione è
   * andata a buon fine: da lì in poi il prossimo avviso è un avviso nuovo e
   * merita una chiave nuova.
   *
   * Facoltativa perché una bozza salvata PRIMA di questo rilascio non ce l'ha:
   * si legge come «tentativo non ancora aperto», che è la verità.
   */
  tentativo?: string;
}

const key = (istat: string) => PREFIX + istat;

/** Una bozza è «vuota» se non c'è né titolo né testo: non vale salvarla. */
export function isEmptyDraft(d: Draft): boolean {
  return d.title.trim() === '' && d.body.trim() === '';
}

export function saveDraft(
  istat: string,
  draft: Draft,
  preset: string,
  tentativo?: string,
): void {
  try {
    if (isEmptyDraft(draft)) {
      sessionStorage.removeItem(key(istat));
      return;
    }
    const payload: StoredDraft = {
      draft,
      preset,
      savedAt: new Date().toISOString(),
      tentativo,
    };
    sessionStorage.setItem(key(istat), JSON.stringify(payload));
  } catch {
    // Storage pieno o disabilitato: la console continua a funzionare senza
    // rete di sicurezza. Non è il momento di fermare chi sta pubblicando.
  }
}

export function loadDraft(istat: string): StoredDraft | null {
  try {
    const raw = sessionStorage.getItem(key(istat));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    // Diffidare del proprio storage: un formato vecchio dopo un rilascio non
    // deve rompere la schermata, deve solo non ripristinare niente.
    if (!parsed?.draft || typeof parsed.draft.title !== 'string') return null;
    if (isEmptyDraft(parsed.draft)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(istat: string): void {
  try {
    sessionStorage.removeItem(key(istat));
  } catch {
    /* vedi sopra */
  }
}

/**
 * C'è una bozza in composizione, di chiunque sia il Comune?
 *
 * Serve al confine di errore (`components/ErrorBoundary.tsx`), che vive fuori
 * dalla sessione e quindi non conosce il codice dell'ente. Lì la domanda non è
 * «quale bozza», è se abbia senso promettere a chi sta guardando una schermata
 * rotta che ricaricare non gli costa quello che aveva scritto.
 *
 * `saveDraft` cancella la chiave quando la bozza è vuota: se una chiave c'è,
 * dentro c'è del testo.
 */
export function hasAnyDraft(): boolean {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      if (sessionStorage.key(i)?.startsWith(PREFIX)) return true;
    }
    return false;
  } catch {
    return false;
  }
}
